import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Container,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import { FaUserPlus, FaCheckCircle, FaExclamationTriangle, FaSpinner } from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import VeloxLogo from '../../assets/Velox-logo-color.png';

const API_URL = process.env.NODE_ENV === 'development' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api');

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

const JoinTeam = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [verifyingToken, setVerifyingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [invitationData, setInvitationData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeStep, setActiveStep] = useState(0);

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. Token is missing.');
      setVerifyingToken(false);
      return;
    }

    verifyInvitationToken();
  }, [token]);

  const verifyInvitationToken = async () => {
    try {
      setVerifyingToken(true);
      
      // Validate hex token format (64 characters, hex string)
      if (!token || typeof token !== 'string' || token.length !== 64 || !/^[a-f0-9]+$/i.test(token)) {
        throw new Error('Invalid token format');
      }

      // For now, we'll just validate the token structure
      // The actual token validation will happen on the backend when the user submits the password
      setTokenValid(true);
      setActiveStep(1);
      
      // Set basic invitation data (we'll get the real data from the backend)
      setInvitationData({
        email: '', // Will be filled by backend
        token: token
      });

    } catch (error) {
      console.error('Token verification failed:', error);
      setError(error.message || 'Invalid or expired invitation token');
      setTokenValid(false);
    } finally {
      setVerifyingToken(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleJoinTeam = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const response = await api.post(`/team/join/${token}`, {
        password: formData.password
      });

      if (response.data.success) {
        // Set user in auth context immediately
        setUser(response.data.data.user);
        
        setSuccess('Welcome to the team!');
        setActiveStep(2);
        
        // Navigate directly to app after 2 seconds
        setTimeout(() => {
          navigate('/app');
        }, 2000);
      }

    } catch (error) {
      console.error('Failed to join team:', error);
      const errorMessage = error.response?.data?.message || 'Failed to join team. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      label: 'Verify Invitation',
      description: 'Checking your invitation...'
    },
    {
      label: 'Set Password',
      description: 'Create your account password'
    },
    {
      label: 'Complete',
      description: 'Welcome to the team!'
    }
  ];

  if (verifyingToken) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card sx={{ borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <FaSpinner size={60} sx={{ mb: 3 }} />
            <Typography variant="h6" color="text.secondary">
              Verifying your invitation...
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (!tokenValid) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card sx={{ borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <FaExclamationTriangle size={60} style={{ color: '#ef4444', marginBottom: 20 }} />
            <Typography variant="h5" fontWeight={600} mb={2}>
              Invalid Invitation
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              {error}
            </Typography>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/login')}
              sx={{ mr: 2 }}
            >
              Go to Login
            </Button>
            <Button 
              variant="contained" 
              onClick={() => navigate('/signup')}
            >
              Create New Account
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Card sx={{ borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 4 }}>
          {/* Velox AI Branding Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img 
              src={VeloxLogo} 
              alt="Velox AI" 
              style={{ 
                height: '60px', 
                width: 'auto',
                marginBottom: '16px'
              }} 
            />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              Powered by Velox AI
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <FaUserPlus size={32} style={{ color: '#3b9efc', marginRight: 16 }} />
            <Box>
              <Typography variant="h4" fontWeight={600}>
                Join Your Team
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Complete your account setup to start collaborating
              </Typography>
            </Box>
          </Box>

          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label} completed={activeStep > index}>
                <StepLabel>
                  <Typography variant="h6" fontWeight={600}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {step.description}
                  </Typography>

                  {index === 0 && tokenValid && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                       Invitation verified successfully
                    </Alert>
                  )}

                  {index === 1 && (
                    <Box>
                      {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                          {error}
                        </Alert>
                      )}

                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Create a secure password for your account:
                        </Typography>
                        
                        <TextField
                          label="Password"
                          type="password"
                          value={formData.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                          error={!!formErrors.password}
                          helperText={formErrors.password}
                          fullWidth
                          margin="normal"
                          required
                        />
                        
                        <TextField
                          label="Confirm Password"
                          type="password"
                          value={formData.confirmPassword}
                          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                          error={!!formErrors.confirmPassword}
                          helperText={formErrors.confirmPassword}
                          fullWidth
                          margin="normal"
                          required
                        />
                      </Box>

                      <Button
                        variant="contained"
                        onClick={handleJoinTeam}
                        disabled={loading}
                        startIcon={loading ? <FaSpinner size={16} /> : <FaUserPlus />}
                        sx={{ 
                          bgcolor: '#3b9efc',
                          '&:hover': { bgcolor: '#2563eb' },
                          px: 4,
                          py: 1
                        }}
                      >
                        {loading ? 'Creating Account...' : 'Join Team'}
                      </Button>
                    </Box>
                  )}

                  {index === 2 && (
                    <Box sx={{ textAlign: 'center' }}>
                      <FaCheckCircle size={60} style={{ color: '#10b981', marginBottom: 20 }} />
                      <Typography variant="h6" fontWeight={600} mb={2}>
                        Welcome to the team!
                      </Typography>
                      <Typography variant="body1" color="text.secondary" mb={3}>
                        {success}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Redirecting to app...
                      </Typography>
                    </Box>
                  )}
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>
    </Container>
  );
};

export default JoinTeam; 